import {
  useCanvasAdd,
  useCanvasCollect,
  useCanvasUpdate,
} from '@frontend/editor/data-access';
import { AutocompleteOptionType } from '@frontend/editor/interface';
import { SocialTypeEnum } from '@frontend/sorghum/interface';
import { Box, styled } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '../autocomplete/autocomplete';
import { ToggleIconType } from './text-button-cell-modal';

interface BlockSettingType extends ToggleIconType {
  handleClose: () => void;
  outputID: string;
  socialType?: SocialTypeEnum;
  onValidateWhenCreateNewBlock: () => void;
}

const BlockSelectorWrapper = styled(Box)(({ theme }) => ({
  '& > div': {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}));

//FIXME: 新增 Block 後 data 內的 targetID 要更新
export const BlockSetting = ({
  outputID,
  handleClose,
  elementID,
  getTargetElement,
  onValidateWhenCreateNewBlock,
  socialType,
}: BlockSettingType) => {
  const { canvasConnectBlock, canvasUpdateData, canvasDisconnectBlock } =
    useCanvasUpdate();
  const { addNodeEditorMenu } = useCanvasAdd();
  const { blocks } = useCanvasCollect();

  const [t] = useTranslation();

  const elementData = useMemo(
    () => getTargetElement(elementID),
    [elementID, getTargetElement],
  );

  const onCreateClick = useCallback(() => {
    canvasUpdateData(elementID, 'targetID', '');
    addNodeEditorMenu(outputID, elementData?.nodeID ?? '');
    // 新增 block 時驗證 text button cell 需不需要顯示紅框
    onValidateWhenCreateNewBlock();
    handleClose();
  }, [
    addNodeEditorMenu,
    canvasUpdateData,
    elementData?.nodeID,
    elementID,
    handleClose,
    onValidateWhenCreateNewBlock,
    outputID,
  ]);

  const handleChange = useCallback(
    (newValue: AutocompleteOptionType | null) => {
      if (newValue) {
        canvasConnectBlock(outputID, newValue.value as string);
      } else {
        canvasDisconnectBlock(outputID);
      }
    },
    [canvasConnectBlock, canvasDisconnectBlock, outputID],
  );

  const optionBlocks = useMemo(() => {
    const newOptionList: AutocompleteOptionType[] = [];
    if (socialType === SocialTypeEnum.FACEBOOK) {
      newOptionList.push({
        label: t('canvas.modal.editTextButton.createBlock'),
        value: 'add',
        divider: true,
        exclude: true,
        onClick: onCreateClick,
      });
    }

    if (blocks.length > 0) {
      blocks
        .filter((i) => i.id !== elementData?.nodeID)
        .forEach((i) => {
          if (i.label) {
            newOptionList.push({
              label: i.label as string,
              value: i.id as string,
            });
          }
        });
    }

    return newOptionList;
  }, [blocks, elementData?.nodeID, onCreateClick, socialType, t]);

  return (
    <BlockSelectorWrapper>
      <Autocomplete
        outputID={outputID}
        placeholder={t('canvas.modal.editTextButton.chooseBlock')}
        onChange={handleChange}
        value={elementData.targetID}
        options={optionBlocks}
      />
    </BlockSelectorWrapper>
  );
};
